// JSON-LD structured-data builders for Reformity Inc.

import {
    SITE_URL,
    SITE_NAME,
    DEFAULT_OG_IMAGE,
    HQ_GEO,
    BUSINESS_ADDRESS,
    EXTERNAL_PROFILES,
    REVIEW_SOURCES,
} from "@/lib/seo-constants";
import { SERVICE_CITIES } from "@/data/service-area";
import { COMPANY } from "@/lib/constants";

const PHONE_E164 = "+1-437-241-6287";

// NOTE: aggregateRating was removed from the LocalBusiness/Service schemas.
// The 157-review total aggregates third-party platform counts that we can't
// substantiate at source (see REBUILD_PLAN §honesty flags), and Google treats
// self-serving LocalBusiness ratings as ignorable at best. Restore only with
// first-party review data collected on this site.

// Every service page on the site, across all three tracks. Keep in sync with
// data/services.js, data/masonry-service.js, data/commercial-services.js and
// data/industrial-services.js (and the STATIC_ROUTES list in backend/server.py).
const SERVICE_OFFERS = [
    // Residential
    { path: "/services/fencing", name: "Fence Installation" },
    { path: "/services/interlocking", name: "Interlocking & Paving" },
    { path: "/services/decking", name: "Custom Decking" },
    { path: "/services/concrete", name: "Concrete & Asphalt Driveways" },
    { path: "/services/landscaping", name: "Landscaping" },
    { path: "/services/masonry", name: "Masonry, Parging & Underpinning" },
    { path: "/services/renovations", name: "Interior Renovations" },
    // Commercial
    { path: "/commercial/services/commercial-fencing-gates", name: "Commercial Fencing & Gates" },
    { path: "/commercial/services/parking-lots-asphalt", name: "Commercial Parking Lots & Asphalt" },
    { path: "/commercial/services/concrete-sidewalks", name: "Commercial Concrete & Sidewalks" },
    { path: "/commercial/services/interlocking-hardscape", name: "Commercial Interlocking & Hardscape" },
    { path: "/commercial/services/commercial-masonry-restoration", name: "Commercial Masonry Restoration" },
    { path: "/commercial/services/landscaping-grounds", name: "Commercial Landscaping & Grounds" },
    { path: "/commercial/services/snow-ice-management", name: "Commercial Snow & Ice Management" },
    { path: "/commercial/services/exterior-renovations-buildouts", name: "Commercial Exterior Renovations" },
    // Industrial
    { path: "/industrial/services/heavy-duty-asphalt-paving", name: "Heavy-Duty Asphalt Paving" },
    { path: "/industrial/services/industrial-concrete", name: "Industrial Concrete" },
    { path: "/industrial/services/site-security-fencing", name: "Industrial Site Security Fencing" },
    { path: "/industrial/services/industrial-masonry-structural", name: "Industrial & Structural Masonry" },
    { path: "/industrial/services/retaining-walls-site-works", name: "Retaining Walls & Site Works" },
    { path: "/industrial/services/bollards-guard-rails-safety", name: "Bollards & Guard Rails" },
    { path: "/industrial/services/exterior-grounds-drainage", name: "Industrial Grounds & Drainage" },
    { path: "/industrial/services/snow-ice-management-industrial", name: "Industrial Snow & Ice Management" },
];

/**
 * Expanded LocalBusiness schema — founder, geo, hours, payment, hasOfferCatalog,
 * sameAs URLs, and full address.
 */
export const localBusinessSchema = () => ({
    "@context": "https://schema.org",
    "@type": "GeneralContractor",
    "@id": `${SITE_URL}/#localbusiness`,
    name: SITE_NAME,
    alternateName: "Reformity",
    description:
        "North York-based exterior and general contractor serving residential, commercial and industrial properties across the Greater Toronto Area — fencing, interlocking, decking, concrete, asphalt, masonry, landscaping, and renovations.",
    url: SITE_URL,
    logo: `${SITE_URL}/favicon-512.png`,
    image: DEFAULT_OG_IMAGE,
    telephone: PHONE_E164,
    email: COMPANY.email,
    priceRange: "$$",
    currenciesAccepted: "CAD",
    paymentAccepted: "Cash, Credit Card, e-Transfer, Cheque",
    foundingDate: "2018",
    founder: { "@type": "Person", name: "Shaheen Rashidi" },
    address: {
        "@type": "PostalAddress",
        streetAddress: BUSINESS_ADDRESS.streetAddress,
        addressLocality: BUSINESS_ADDRESS.addressLocality,
        addressRegion: BUSINESS_ADDRESS.addressRegion,
        postalCode: BUSINESS_ADDRESS.postalCode,
        addressCountry: BUSINESS_ADDRESS.addressCountry,
    },
    geo: {
        "@type": "GeoCoordinates",
        latitude: HQ_GEO.lat,
        longitude: HQ_GEO.lon,
    },
    areaServed: SERVICE_CITIES.map((c) => ({
        "@type": "City",
        name: c.name,
    })),
    openingHoursSpecification: [
        {
            "@type": "OpeningHoursSpecification",
            dayOfWeek: [
                "Monday",
                "Tuesday",
                "Wednesday",
                "Thursday",
                "Friday",
                "Saturday",
            ],
            opens: "08:00",
            closes: "18:00",
        },
    ],
    sameAs: [
        EXTERNAL_PROFILES.homestars,
        EXTERNAL_PROFILES.houzz,
        EXTERNAL_PROFILES.bbb,
        EXTERNAL_PROFILES.bark,
        EXTERNAL_PROFILES.google,
        EXTERNAL_PROFILES.facebook,
        EXTERNAL_PROFILES.instagram,
        EXTERNAL_PROFILES.youtube,
        EXTERNAL_PROFILES.linkedin,
    ],
    hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: "Contracting Services",
        itemListElement: SERVICE_OFFERS.map((o) => ({
            "@type": "Offer",
            itemOffered: {
                "@type": "Service",
                name: o.name,
                url: `${SITE_URL}${o.path}`,
            },
        })),
    },
});

export const serviceSchema = ({ name, description, slug, path }) => ({
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: name,
    provider: {
        "@type": "GeneralContractor",
        name: SITE_NAME,
        url: SITE_URL,
    },
    areaServed: {
        "@type": "AdministrativeArea",
        name: "Greater Toronto Area",
    },
    description,
    // `path` lets non-residential tracks emit their own canonical service URL.
    url: `${SITE_URL}${path || `/services/${slug}`}`,
});

/**
 * ItemList of Review objects for the /reviews page.
 * Used for Google rich-result eligibility on review snippets.
 */
export const reviewsItemListSchema = (testimonials) => ({
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Customer Reviews — Reformity Inc.",
    itemListOrder: "https://schema.org/ItemListOrderDescending",
    numberOfItems: testimonials.length,
    itemListElement: testimonials.map((t, i) => {
        const source = REVIEW_SOURCES[t.source];
        const review = {
            "@type": "Review",
            reviewBody: t.fullQuote || t.quote,
            author: { "@type": "Person", name: t.name },
            reviewRating: {
                "@type": "Rating",
                ratingValue: String(t.rating),
                bestRating: "5",
                worstRating: "1",
            },
            itemReviewed: {
                "@type": "GeneralContractor",
                name: SITE_NAME,
                url: SITE_URL,
            },
            publisher: {
                "@type": "Organization",
                name: t.source,
                ...(source ? { url: source.profileUrl } : {}),
            },
            ...(t.date ? { datePublished: t.date } : {}),
        };
        return {
            "@type": "ListItem",
            position: i + 1,
            item: review,
        };
    }),
});

export const faqPageSchema = (faqs) => ({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: {
            "@type": "Answer",
            text: f.a,
        },
    })),
});

export const breadcrumbListSchema = (items) => {
    const list =
        items[0]?.path === "/" ? items : [{ name: "Home", path: "/" }, ...items];
    return {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: list.map((it, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: it.name,
            item: `${SITE_URL}${it.path}`,
        })),
    };
};

/**
 * WebPage schema — used by the per-source review deep-link pages so each
 * URL announces its own name + description independently of the generic
 * LocalBusiness card.
 */
export const webPageSchema = ({ name, description, path }) => ({
    "@context": "https://schema.org",
    "@type": "WebPage",
    name,
    description,
    url: `${SITE_URL}${path}`,
    isPartOf: {
        "@type": "WebSite",
        name: SITE_NAME,
        url: SITE_URL,
    },
});

/**
 * Root WebSite schema (+ Organization publisher + SearchAction). Declares the
 * whole site as one entity to search + AI answer engines, and exposes the blog
 * search so a sitelinks searchbox can trigger. Mounted on the splash root.
 */
export const websiteSchema = () => ({
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    name: SITE_NAME,
    url: SITE_URL,
    publisher: {
        "@type": "GeneralContractor",
        "@id": `${SITE_URL}/#localbusiness`,
        name: SITE_NAME,
    },
    potentialAction: {
        "@type": "SearchAction",
        target: {
            "@type": "EntryPoint",
            urlTemplate: `${SITE_URL}/blog?q={search_term_string}`,
        },
        "query-input": "required name=search_term_string",
    },
});
